<template>
  <section>
    <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
      <div class="tw-col-span-1 tw-mb-2">
        <cl-form-group>
          <cl-form-label
            label-for="name"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("name") }}
          </cl-form-label>
          <cl-form-input
            :id="'name'"
            v-model:value="form.name"
            :name="'name'"
            :type="'text'"
            :placeholder="t('msp-name')"
            :state="!v$.name.$dirty ? undefined : !v$.name.$error"
            :data-test-id="'edit-msp-input-name'"
            @blur="handleBlur('name')"
            @input="v$.name.$reset()"
          />
          <span v-if="!v$.name.$error" class="tw-text-sm">
            {{ t("customer-name-hint") }}
          </span>
          <span
            v-if="v$.name.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.name.required.$response">
              {{ t("required-field") }}
            </span>
            <span
              v-else-if="
                !v$.name.maxLength.$response || !v$.name.minLength.$response
              "
            >
              {{ t("customer-name-validation") }}
            </span>
          </span>
        </cl-form-group>
      </div>
    </div>
    <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
      <div class="tw-col-span-1 tw-mb-2">
        <cl-form-group>
          <cl-form-label
            label-for="description"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("description-label") }}
          </cl-form-label>
          <cl-form-input
            :id="'description'"
            v-model:value="v$.description.$model"
            :name="'description'"
            :type="'text'"
            :placeholder="t('description-placeholder')"
            :data-test-id="'edit-msp-input-description'"
            :state="!v$.description.$dirty ? undefined : !v$.description.$error"
          />
          <span
            v-if="v$.description.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            <span v-if="!v$.description.maxLength.$response">
              {{ t("description-255-characters") }}
            </span>
          </span>
        </cl-form-group>
      </div>
    </div>
    <div class="tw-grid tw-grid-cols-2 tw-gap-5 max-md:tw-grid-cols-1">
      <div class="tw-col-span-1 tw-mb-2">
        <cl-form-group>
          <cl-form-label
            label-for="country"
            class="hover:tw-cursor-pointer tw-mr-4"
            >{{ t("country") }}</cl-form-label
          >
          <!-- TODO - @Rob to make the search dropdown as a component:                -->
          <!-- https://gitlab.titanhq.com/titanhq/dev/scrum/team-lynxes/-/issues/270  -->
          <!--                <div ref="countriesDropdownWrapper" class="tw-relative">-->
          <div ref="countriesDropdownWrapper">
            <div
              :class="[
                isAvailableToUpdateCountry
                  ? ''
                  : 'tw-pointer-events-none tw-opacity-30',
              ]"
              class="tw-relative"
            >
              <input
                v-model="countrySearchQuery"
                type="text"
                data-test-id="edit-msp-country"
                :class="'tw-block tw-w-full tw-rounded tw-border tw-py-2 tw-pl-9 tw-pr-2 tw-bg-white tw-text-primary-grey-700 tw-outline-none tw-ring-offset-1 tw-transition-all tw-text-base tw-border-primary-grey-100/80 focus:tw-border-primary-grey-500 focus:tw-ring-4 focus:tw-ring-primary-grey-500/10 disabled:tw-cursor-not-allowed disabled:tw-bg-primary-grey-50 disabled:tw-text-primary-grey-700/50 disabled:tw-outline-none read-only:tw-cursor-default read-only:tw-bg-primary-grey-50 read-only:tw-text-primary-grey-700/50 read-only:tw-outline-none'"
                :placeholder="t('search-countries')"
                :disabled="!isAvailableToUpdateCountry"
                @input="countryFilterOptions"
                @click="openCountryDropdown"
                @blur="handleBlur('country')"
              />
              <div
                class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3"
              >
                <cb-icon-search
                  :width="20"
                  class="tw-pointer-events-none tw-text-primary-grey-200"
                />
              </div>
              <div
                class="tw-absolute tw-top-2 tw-right-3 tw-pointer-events-none"
              >
                <cb-icon-chevron-down
                  v-if="isCountryDropdownOpen"
                  class="tw-text-primary-grey-200 tw-transition-transform tw-rotate-180"
                />
                <cb-icon-chevron-down
                  v-if="!isCountryDropdownOpen"
                  class="tw-text-primary-grey-200 tw-transition-transform"
                />
              </div>
            </div>
            <div class="tw-absolute tw-z-50">
              <ul
                v-if="isCountryDropdownOpen"
                ref="countriesDropdownList"
                :data-test-id="'update-country-list-dropdown'"
                class="tw-z-auto tw-mt-1 tw-w-[300px] tw-max-h-48 tw-overflow-y-auto tw-bg-white tw-border tw-border-primary-grey-100 tw-rounded tw-shadow-lg tw-text-primary-grey-700"
                @click="stopPropagation"
              >
                <li
                  v-if="filteredCountriesList.length === 0"
                  class="tw-py-3 tw-px-3 tw-text-primary-grey-300"
                >
                  {{ t("no-matches") }}
                </li>
                <li
                  v-for="(country, index) in filteredCountriesList"
                  v-else
                  :key="index"
                  class="tw-py-2 tw-px-3 tw-cursor-pointer hover:tw-bg-primary-grey-50"
                  :data-test-id="`update-${country.name}`"
                  @click="selectCountry(country)"
                >
                  {{ country.name }}
                </li>
              </ul>
            </div>
            <span
              v-if="v$.country.$error"
              class="tw-text-system-error-500 tw-text-sm"
            >
              <span v-if="!v$.country.required.$response">
                {{ t("required-field") }}
              </span>
            </span>
          </div>
        </cl-form-group>
      </div>
      <div class="tw-col-span-1 tw-mb-2">
        <cl-form-group>
          <cl-form-label
            label-for="customerID"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("maxio-customer-id") }}
          </cl-form-label>
          <cl-form-input
            :id="'customerID'"
            disabled
            v-model:value="form.customerID"
            :name="'customerID'"
            :type="'text'"
            :maxlength="50"
            :state="!v$.customerID.$dirty ? undefined : !v$.customerID.$error"
            :placeholder="t('maxio-customer-id')"
            :data-test-id="'edit-msp-input-customerID'"
            @blur="handleBlur('customerID')"
            @input="v$.customerID.$reset()"
            @keydown="guardNumericInput"
            @paste="guardNumericPaste"
            class="tw-pointer-events-none tw-opacity-30"
          />
          <span
            v-if="v$.customerID.$error"
            class="tw-text-system-error-500 tw-text-sm"
          >
            {{ t("maxio-id") }}
          </span>
        </cl-form-group>
      </div>
    </div>
  </section>
</template>
<script lang="ts">
type Form = {
  name: string;
  description: string;
  country: string;
  customerID: number;
  uuid: string;
};

interface Props {
  form: Form;
  isAvailableToUpdateCountry: boolean;
  organisationObject: Organisation;
}
</script>
<script setup lang="ts">
import { reactive, watch, watchEffect, ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import { COUNTRIES } from "@/constants/countries";
import { CountryOption } from "../t-update-msps.types";
import { Organisation } from "@/types/organisatio";
import { guardNumericInput, guardNumericPaste } from "@/utils/inputGuards";

const props = defineProps<Props>();
const form = reactive<Form>({ ...props.form });
const organisationObject = ref(props.organisationObject);
const { t } = useI18n();

const filteredCountriesList = ref<CountryOption[]>([]);
const countryOptions = ref<CountryOption[]>(COUNTRIES);
const isCountryDropdownOpen = ref(false);
const countrySearchQuery = ref("");
const selectedOption = ref<CountryOption | null>(null);
const countriesDropdownWrapper = ref<HTMLElement | null>(null);
const countriesDropdownList = ref<HTMLElement | null>(null);

const emit = defineEmits<{
  (e: "update:form", form: Form): void;
  (e: "formValidation", isValid: boolean): void;
}>();

const rules = computed(() => ({
  name: { required, minLength: minLength(3), maxLength: maxLength(100) },
  description: { maxLength: maxLength(255) },
  country: { required },
  customerID: {},
}));
const v$ = useVuelidate(rules, form);

const handleBlur = async (field: string) => {
  const isValid = await v$.value[field].$validate();
  if (!isValid) {
    return;
  }
};

const openCountryDropdown = () => {
  isCountryDropdownOpen.value = true;
  filteredCountriesList.value = countryOptions.value;
};
const countryFilterOptions = () => {
  if (countrySearchQuery.value === "") {
    filteredCountriesList.value = countryOptions.value;
    form.country = "";
    return;
  }
  filteredCountriesList.value = countryOptions.value.filter(
    (option) =>
      option.name
        .toLowerCase()
        .includes(countrySearchQuery.value.toLowerCase()) ||
      option.code
        .toLowerCase()
        .includes(countrySearchQuery.value.toLowerCase()),
  );
};
const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

const selectCountry = (option: CountryOption) => {
  selectedOption.value = option;
  isCountryDropdownOpen.value = false;
  countrySearchQuery.value = option.name;
  form.country = option.numeric;
};

const closeDropdownOnClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (
    isCountryDropdownOpen.value &&
    target !== countriesDropdownWrapper.value &&
    !countriesDropdownWrapper.value?.contains(target) &&
    target !== countriesDropdownList.value &&
    !countriesDropdownList.value?.contains(target)
  ) {
    isCountryDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", closeDropdownOnClickOutside);
  updatedSelectedCountry();
});

const updatedSelectedCountry = () => {
  const filterCountries = COUNTRIES;
  const organisationSelectedCountry = organisationObject.value.country;

  const selectedCountry = filterCountries.find(
    (item) => item.numeric === organisationSelectedCountry,
  );
  if (selectedCountry) {
    selectCountry(selectedCountry as unknown as CountryOption);
  } else {
    form.country = "";
    countrySearchQuery.value = "";
    openCountryDropdown();
  }
};

watch(form, (newValue) => {
  emit("update:form", newValue);
});

watchEffect(() => {
  emit("formValidation", v$.value.$invalid);
});
</script>
